/**
 * useProductsQuery — server-side paginated + filtered + sorted products.
 *
 * The Katalog > Ürünler page uses this hook INSTEAD of pulling products from
 * the SizingStore, because a 20k+ catalog can't be rendered client-side.
 *
 * Params surface (all optional):
 *   page, limit                                  — pagination
 *   q                                            — free-text search (name/SKU)
 *   sort, dir                                    — sort key + direction
 *   tablePresence: "all"|"has"|"none"|"exempt"   — table filter tri-state
 *   modelPresence: "all"|"has"|"none"
 *   fitPresence:   "all"|"has"|"none"
 *   imagePresence: "all"|"has"|"none"
 *   tableIds, modelIds, fitIds                   — string[] (specific IDs)
 *   groupLabels, seriesLabels                    — string[] (label filter)
 *   categoryPaths                                — string[] ("A > B" paths)
 *
 * Contract:
 *   - Search is debounced (350ms) internally by the caller if needed.
 *   - `refetch()` re-runs the current query (used after mutations).
 *   - `patchLocalItem(id, patch)` optimistically updates a row in the
 *      current page so the UI reflects a mutation immediately.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const csv = (arr) => (Array.isArray(arr) && arr.length ? arr.join(",") : "");

export function useProductsQuery(params) {
    const {
        page = 1,
        limit = 50,
        q = "",
        sort = "name",
        dir = "asc",
        tablePresence = "all",
        modelPresence = "all",
        fitPresence = "all",
        imagePresence = "all",
        tableIds = [],
        modelIds = [],
        fitIds = [],
        groupLabels = [],
        seriesLabels = [],
        categoryPaths = [],
        // status axis: "all" | "active" | "passive". Default active — merchant sees
        // only working catalog rows unless they explicitly ask for the passive slice.
        statusFilter = "active",
        // External bump — when this value changes the query refetches. Used
        // by the SizingStore to signal that a product mutation happened
        // (e.g. from a dialog or bulk action), so the page stays fresh
        // without wiring each mutation callback back into this hook.
        version = 0,
    } = params;

    const [items, setItems] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const abortRef = useRef(null);
    const nonce = useRef(0);

    const fetchNow = useCallback(async () => {
        // Cancel any in-flight request from a superseded query.
        if (abortRef.current) abortRef.current.abort();
        const controller = new AbortController();
        abortRef.current = controller;
        const myNonce = ++nonce.current;

        const skip = Math.max(0, (page - 1) * limit);
        const query = {
            skip, limit, q: q || undefined,
            sort, dir,
            tablePresence: tablePresence !== "all" ? tablePresence : undefined,
            modelPresence: modelPresence !== "all" ? modelPresence : undefined,
            fitPresence: fitPresence !== "all" ? fitPresence : undefined,
            imagePresence: imagePresence !== "all" ? imagePresence : undefined,
            tableIds: csv(tableIds) || undefined,
            modelIds: csv(modelIds) || undefined,
            fitIds: csv(fitIds) || undefined,
            groupLabels: csv(groupLabels) || undefined,
            seriesLabels: csv(seriesLabels) || undefined,
            categoryPaths: csv(categoryPaths) || undefined,
            statusFilter: statusFilter !== "active" ? statusFilter : undefined,
        };
        setLoading(true);
        try {
            const { data } = await api.get(`/products`, {
                params: query,
                signal: controller.signal,
            });
            if (myNonce !== nonce.current) return; // superseded
            setItems(data?.items || []);
            setTotal(Number(data?.total || 0));
            setError(null);
        } catch (e) {
            if (e?.name === "CanceledError" || e?.code === "ERR_CANCELED") return;
            console.error("[useProductsQuery] fetch failed:", e);
            setError(e);
            setItems([]);
            setTotal(0);
        } finally {
            if (myNonce === nonce.current) setLoading(false);
        }
    }, [
        page, limit, q, sort, dir,
        tablePresence, modelPresence, fitPresence, imagePresence,
        // Arrays are joined so referential changes still trigger effects.
        csv(tableIds), csv(modelIds), csv(fitIds),
        csv(groupLabels), csv(seriesLabels), csv(categoryPaths),
        statusFilter,
        version,
    ]);

    useEffect(() => { fetchNow(); }, [fetchNow]);

    // Optimistic in-place patch for a row on the current page. Callers use
    // this from mutation handlers so the UI updates before the server round-
    // trip; a follow-up refetch keeps the authoritative state honest.
    const patchLocalItem = useCallback((id, patch) => {
        setItems((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
    }, []);

    // Optimistic removal — used after delete so pagination bounds stay right.
    const removeLocalItems = useCallback((ids) => {
        const idSet = new Set(ids);
        setItems((prev) => prev.filter((p) => !idSet.has(p.id)));
    }, []);

    return {
        items,
        total,
        loading,
        error,
        refetch: fetchNow,
        patchLocalItem,
        removeLocalItems,
    };
}
